import {DragEvent, useLayoutEffect, useRef} from 'react'
import onResize from 'simple-element-resize-detector'
import {HereMapAtomProps} from '../@atoms/to_content/map_h/here_map.atom'
import map_marker from '../assets/images/marker.png'

const useHereMap = (props: HereMapAtomProps) => {
  // Create a reference to the HTML element we want to put the map on
  const mapRef = useRef(null)
  const {lat, lng, api_key, app_id, ap_position, setAddressBasedOnGeoCode} =
    props
  const {H} = window

  useLayoutEffect(
    () => {
      // `mapRef.current` will be `undefined` when this hook first runs; edge case that
      if (!mapRef.current) return

      // initialize communication with the platform
      const platform = new H.service.Platform({
        apikey: api_key,
        app_id,
      })
      const defaultLayers = platform.createDefaultLayers()

      // initialize a map
      const map = new H.Map(mapRef.current, defaultLayers.vector.normal.map, {
        center: {lat, lng},
        zoom: 16,
        pixelRatio: window.devicePixelRatio || 1,
      })

      // add a resize listener to make sure that the map occupies the whole container
      onResize(mapRef.current, function () {
        map.getViewPort().resize()
      })

      // MapEvents enables the event system
      // Behavior implements default interactions for pan/zoom (also on mobile touch environments)
      const behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(map))

      // Create the default UI:
      H.ui.UI.createDefault(map, defaultLayers)

      // Add a  draggable or non-draggable marker to the map
      const iconMarker = new H.map.Icon(`${map_marker}`, {
        size: {w: 22, h: 32},
        position: 'relative',
        margin: {bottom: 20},
      })
      const marker = new H.map.Marker(
        {lat, lng},
        {
          icon: iconMarker,
          // mark the object as volatile for the smooth dragging
          volatility: true,
        },
      )
      marker.draggable = ap_position === 'EDIT_LOCATION_SCREEN'
      map.addObject(marker)

      // Add drag event for draggable marker
      if (ap_position === 'EDIT_LOCATION_SCREEN') {
        // disable the default draggability of the underlying map
        // and calculate the offset between mouse and target's position
        // when starting to drag a marker object:
        const dragStartEventHandler = (ev: DragEvent & typeof map) => {
          const {target} = ev
          const pointer = ev.currentPointer
          if (target instanceof H.map.Marker) {
            const targetPosition = map.geoToScreen(target.getGeometry())
            target.offset = new H.math.Point(
              pointer.viewportX - targetPosition.x,
              pointer.viewportY - targetPosition.y,
            )
            behavior.disable()
          }
        }

        map.addEventListener('dragstart', dragStartEventHandler, false)

        // re-enable the default draggability of the underlying map
        // when dragging has completed
        const dragEndEventHandler = (ev: DragEvent & typeof map) => {
          const {target} = ev
          if (target instanceof H.map.Marker) {
            const geometry = marker.getGeometry()
            map.setCenter({lat: geometry.lat, lng: geometry.lng})
            behavior.enable()
            if (setAddressBasedOnGeoCode)
              setAddressBasedOnGeoCode(geometry.lat, geometry.lng).catch(() => {
                marker.setGeometry({lat, lng})
                map.setCenter({lat, lng})
              })
          }
        }

        map.addEventListener('dragend', dragEndEventHandler, false)

        // Listen to the drag event and move the position of the marker
        // as necessary
        const dragEventHandler = (ev: DragEvent & typeof map) => {
          const {target} = ev
          const pointer = ev.currentPointer
          if (target instanceof H.map.Marker) {
            target.setGeometry(
              map.screenToGeo(
                pointer.viewportX - target.offset.x,
                pointer.viewportY - target.offset.y,
              ),
            )
          }
        }

        map.addEventListener('drag', dragEventHandler, false)
      }

      /*eslint-disable */
      return () => {
        map.dispose()
      }
      /* eslint-enable */
    }, // eslint-disable-next-line
    [
      api_key,
      app_id,
      lat,
      lng,
      ap_position,
      mapRef,
      H.Map,
      H.map.Icon,
      H.map.Marker,
      H.mapevents.Behavior,
      H.mapevents.MapEvents,
      H.service.Platform,
      H.ui.UI,
    ],
  )

  return mapRef
}

export default useHereMap
